import type { ReactNode } from 'react'

/**
 * Pages légales.
 *
 * Le contenu décrit ce que le code fait réellement — flux Stripe, données stockées
 * par les Cloud Functions, mesure d'audience sous consentement. Toute évolution du
 * produit doit se répercuter ici, sous peine de promettre autre chose que la réalité.
 */

const OWNER = 'Ludovic Bouguerra'
const CITY = 'Marseille, France'
const CONTACT = 'https://stream.studio/contact-us/'
const UPDATED = '11 August 2026'

function Doc({ title, updated, children }: {
  title: string
  updated: string
  children: ReactNode
}) {
  return (
    <main className="shell legal">
      <h1>{title}</h1>
      <p className="legal-meta">Last updated: {updated}</p>
      {children}
      <hr />
      <p className="legal-meta">
        {OWNER} — {CITY}. Questions: <a href={CONTACT}>contact form</a>.
      </p>
    </main>
  )
}

export function Terms() {
  return (
    <Doc title="Terms of Use" updated={UPDATED}>
      <h2>1. Who you are dealing with</h2>
      <p>
        AppSwitch is published by {OWNER}, {CITY}. These terms govern your use of the
        AppSwitch application for macOS and of this website.
      </p>

      <h2>2. The free version</h2>
      <p>
        The window switcher is free and complete. No account, no payment and no
        registration are required to use it.
      </p>

      <h2>3. AppSwitch Pro licence</h2>
      <p>
        AppSwitch Pro is a <strong>one-time purchase</strong>, not a subscription.
        Nothing is renewed and nothing is charged again. Buying grants you a personal,
        non-exclusive, non-transferable licence to use the Pro features.
      </p>
      <p>
        One licence key may be activated on <strong>two Macs</strong>. You can free a
        seat at any time from your account area or from within the app. Sharing your key
        publicly or reselling it is not permitted.
      </p>

      <h2>4. Payment</h2>
      <p>
        Payments are processed by Stripe. We never see or store your card details. The
        price shown at checkout is the price charged, taxes included where applicable.
      </p>

      <h2>5. Right of withdrawal</h2>
      <p>
        If you are a consumer in the European Union, you have a statutory right to withdraw
        from this purchase within <strong>14 days</strong> of the day it was made, without
        giving a reason. Write to us and we will refund you in full.
      </p>
      <p>
        Outside that period, or where the right does not apply, refunds are at our
        discretion. If AppSwitch does not do what this page says it does, tell us — we would
        rather refund you than keep money you are unhappy about.
      </p>

      <h2>6. Updates and availability</h2>
      <p>
        Updates are included in your purchase for the current major version. We may stop
        distributing or supporting AppSwitch at any time; if that happens, keys already
        issued keep working on machines already activated.
      </p>

      <h2>7. System permissions</h2>
      <p>
        AppSwitch requires the macOS Accessibility permission to capture ⌥Tab, list your
        windows and bring them to the front. Screen Recording is optional and only powers
        window previews. You grant these permissions yourself in System Settings and can
        revoke them at any time.
      </p>

      <h2>8. No warranty</h2>
      <p>
        AppSwitch is provided “as is”, without warranty of any kind. We do not guarantee
        that it will be free of defects or compatible with every configuration or every
        future version of macOS.
      </p>

      <h2>9. Limitation of liability</h2>
      <p>
        To the extent permitted by law, our total liability is limited to the amount you
        paid for the licence. Nothing in these terms limits liability for fraud, death or
        personal injury caused by negligence, or any liability that cannot lawfully be
        excluded — including consumers’ statutory rights, which remain unaffected.
      </p>

      <h2>10. Governing law</h2>
      <p>
        These terms are governed by French law. Disputes fall under the courts of
        Marseille, except where consumer protection law entitles you to bring proceedings
        in the courts of your own place of residence.
      </p>

      <h2>11. Changes</h2>
      <p>
        We may update these terms. The date above always reflects the version in force.
        Changes never apply retroactively to a purchase already made.
      </p>
    </Doc>
  )
}

export function Privacy() {
  return (
    <Doc title="Privacy Policy" updated={UPDATED}>
      <h2>The short version</h2>
      <p>
        <strong>The app collects nothing.</strong> No window content, no window title and
        no keystroke ever leaves your Mac. The only thing AppSwitch sends over the network
        is a licence check, and only if you bought Pro.
      </p>

      <h2>1. What the app sends</h2>
      <p>
        If you activate a Pro licence, the app sends your licence key, a hardware
        identifier for your Mac and its name — so that we can count activations against
        the two-seat limit and show you which machines are in use. Nothing else is
        transmitted. The free version makes no network requests at all.
      </p>
      <p>
        Window previews are produced and displayed locally. They are never uploaded,
        stored or transmitted.
      </p>

      <h2>2. What we store when you buy</h2>
      <p>We keep, for each purchase: the licence key, the email address you used at
        checkout, the Stripe payment reference, and the list of activated machines
        (identifier, name, activation date, last seen date). That is the whole record.</p>
      <p>We never receive your card details. Stripe handles payment and holds that data
        as an independent controller under its own policy.</p>

      <h2>3. Processors we rely on</h2>
      <ul>
        <li><strong>Stripe</strong> — payment processing.</li>
        <li><strong>Google Firebase</strong> — hosting, authentication, database and
          server functions.</li>
        <li><strong>Google Analytics</strong> — website audience measurement, loaded
          <em> only</em> if you accept it in the banner. Decline and no analytics cookie
          is ever set.</li>
      </ul>

      <h2>4. Account on this website</h2>
      <p>
        Creating an account is optional and only serves to find your licence key again and
        manage your activations. We store the email address and, if you sign in with
        Google or Apple, the identifier they give us. Nothing more.
      </p>

      <h2>5. How long we keep it</h2>
      <p>
        Licence records are kept as long as the licence exists, because they are what lets
        us honour it. Ask us to delete your account and we will remove it along with its
        activations; the licence key then stops working.
      </p>

      <h2>6. Your rights</h2>
      <p>
        Under the GDPR you may access, correct, export or delete your data, and object to
        its processing. Write to us via the <a href={CONTACT}>contact form</a> and we will
        answer within 30 days. You may also lodge a complaint with the CNIL.
      </p>

      <h2>7. Children</h2>
      <p>AppSwitch is not directed at children and we do not knowingly collect data from
        them.</p>

      <h2>8. Changes</h2>
      <p>
        We may update this policy. The date above always reflects the version in force.
      </p>
    </Doc>
  )
}
